import { User } from '../services/usersService';

// List of roles relevant for staff management (exclude roles from other apps)
export const RELEVANT_STAFF_ROLES = new Set([
  'sysAdmin',
  'admin',
  'orgAdmin',
  'storeManager',
  'staff',
  'cashier',
  'inventoryClerk',
  'auditor',
  'sysSupport',
]);

/**
 * Filter out irrelevant roles for staff management display
 */
export const filterRelevantRoles = (roles: string[] = []): string[] => {
  return roles.filter((role) => RELEVANT_STAFF_ROLES.has(role));
};

// Role hierarchy for filtering users
export const ROLE_HIERARCHY: Record<string, number> = {
  sysAdmin: 5,
  admin: 4,
  orgAdmin: 4,
  storeManager: 3,
  staff: 2,
  cashier: 1,
};

// Helper function to get user's highest role level
export const getUserHighestRoleLevel = (roles: string[] = []): number => {
  if (!roles || roles.length === 0) return 0;
  return Math.max(...roles.map((role) => ROLE_HIERARCHY[role] || 0));
};

// Helper function to check if a role should be visible to the current user
export const shouldShowRole = (userRole: string, viewerRoleLevel: number): boolean => {
  const roleLevel = ROLE_HIERARCHY[userRole] || 0;

  // sysAdmin can see all roles
  if (viewerRoleLevel >= 5) return true;

  // orgAdmin/admin can see all roles including sysAdmin
  if (viewerRoleLevel >= 4) {
    return true;
  }

  // storeManager can see all except sysAdmin and orgAdmin/admin
  if (viewerRoleLevel >= 3) {
    return roleLevel < 4;
  }

  // staff can see all except sysAdmin, orgAdmin/admin, and storeManager
  if (viewerRoleLevel >= 2) {
    return roleLevel < 3;
  }

  // cashier can see all except sysAdmin, orgAdmin/admin, storeManager, and staff
  if (viewerRoleLevel >= 1) {
    return roleLevel < 2;
  }

  return false;
};

/**
 * Filter users based on role hierarchy
 * Used to hide higher-level users (admins) from lower-level users (managers, staff)
 */
export const filterUsersByRole = (
  users: User[],
  viewerRoles: string[] = []
): User[] => {
  const viewerRoleLevel = getUserHighestRoleLevel(viewerRoles);
  const isStoreManager = viewerRoleLevel === 3; // storeManager level

  return users.filter((user) => {
    // If user has no roles, show them
    if (!user.roles || user.roles.length === 0) return true;

    // Store managers should not see orgAdmin users at all
    if (isStoreManager && user.roles.includes('orgAdmin')) {
      return false;
    }

    // Check if any of the user's roles should be visible
    return user.roles.some((role) => shouldShowRole(role, viewerRoleLevel));
  });
};
